import axios from "axios";
import { rootStore } from "@/mobx/store";
import { API_URL, API_URL_DEV, API_URL_LOCAL } from "@/config/const";

// API URL 결정
let apiUrl = `${API_URL}`;
const currentUrl = window.location.href;

if (currentUrl.includes("dev.chewingpay.com")) {
  apiUrl = `${API_URL_DEV}`;
} else if (currentUrl.includes("chewingpay.com")) {
  apiUrl = `${API_URL}`;
} else if (currentUrl.includes("localhost")) {
  apiUrl = `${API_URL_LOCAL}`;
}

const MAX_REFRESH_ATTEMPTS = 3;

// 토큰 갱신 함수
export const refreshAccessToken = async (): Promise<string> => {
  console.log("[TOKEN REFRESH] ========== START ==========");
  const refreshToken = localStorage.getItem("refresh_token");
  if (!refreshToken) {
    console.error("[TOKEN REFRESH] No refresh token in localStorage");
    throw new Error("No refresh token available");
  }

  // Refresh token 만료 확인
  try {
    const payload = JSON.parse(atob(refreshToken.split(".")[1]));
    const currentTime = Date.now() / 1000;
    const timeUntilExpiry = payload.exp - currentTime;

    if (timeUntilExpiry <= 0) {
      console.error("[TOKEN REFRESH] Refresh token already expired!");
    }
  } catch (e) {
    console.error("[TOKEN REFRESH] Failed to parse refresh token:", e);
  }

  // 재시도 루프로 변경 (재귀 호출 대신)
  let lastError: any = null;

  for (let attempt = 1; attempt <= MAX_REFRESH_ATTEMPTS; attempt++) {
    try {
      // 토큰 갱신 요청에 타임아웃 설정
      const response = await axios.post(
        `${apiUrl}/office/reissueToken`,
        { refreshToken },
        {
          timeout: 10000, // 10초 타임아웃
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      // result_code 확인 (0이면 성공)
      if (response.data.result_code !== 0) {
        console.error(
          "[TOKEN REFRESH] result_code is not 0:",
          response.data.result_code
        );
        console.error("[TOKEN REFRESH] result_msg:", response.data.result_msg);
        throw new Error(
          `Token refresh failed: ${response.data.result_msg || "Unknown error"}`
        );
      }

      let accessToken: string | null = null;
      let newRefreshToken: string | null = null;

      // result_data에서 토큰 추출
      if (response.data.result_data?.accessToken) {
        accessToken = response.data.result_data.accessToken;
        newRefreshToken = response.data.result_data.refreshToken;
      } else if (response.data.accessToken) {
        // 직접 accessToken이 있는 경우 (fallback)
        accessToken = response.data.accessToken;
        newRefreshToken = response.data.refreshToken;
      } else {
        console.error(
          "[TOKEN REFRESH] No accessToken found in response structure"
        );
        console.error(
          "[TOKEN REFRESH] Available keys:",
          Object.keys(response.data)
        );
      }

      if (accessToken) {
        // MobX store의 setToken 메서드 사용
        rootStore.setToken(accessToken);

        // 새로운 refresh token이 있다면 저장
        if (newRefreshToken) {
          localStorage.setItem("refresh_token", newRefreshToken);
        }

        // 토큰 저장 확인
        const savedToken = rootStore.token;
        console.log(
          "[TOKEN REFRESH] Verification - saved token exists:",
          !!savedToken
        );
        console.log("[TOKEN REFRESH] ========== SUCCESS ==========");

        return accessToken;
      } else {
        throw new Error("Token refresh failed - no access token in response");
      }
    } catch (error: any) {
      lastError = error;
      console.error(`[TOKEN REFRESH] Attempt ${attempt} failed`);
      console.error("[TOKEN REFRESH] Error type:", error.constructor.name);
      console.error("[TOKEN REFRESH] Error message:", error.message);
      console.error("[TOKEN REFRESH] Error code:", error.code);
      console.error(
        "[TOKEN REFRESH] Error response status:",
        error.response?.status
      );
      console.error(
        "[TOKEN REFRESH] Error response data:",
        error.response?.data
      );
      console.error("[TOKEN REFRESH] Error stack:", error.stack);

      // 네트워크 오류가 아니거나 마지막 시도인 경우 즉시 실패
      const isNetworkError =
        error.code === "ERR_NETWORK" || error.message === "Network Error";
      console.log("[TOKEN REFRESH] Is network error:", isNetworkError);
      console.log(
        "[TOKEN REFRESH] Is last attempt:",
        attempt === MAX_REFRESH_ATTEMPTS
      );

      if (!isNetworkError || attempt === MAX_REFRESH_ATTEMPTS) {
        // 네트워크 오류가 아니거나 최대 시도 횟수에 도달한 경우
        console.error(
          "[TOKEN REFRESH] Not retrying - non-network error or max attempts reached"
        );
        console.error("[TOKEN REFRESH] ========== FAILED ==========");
        throw error;
      }

      // 네트워크 오류이고 아직 재시도 가능한 경우
      const waitTime = 1000 * attempt; // 점진적 대기 시간
      console.log(
        `[TOKEN REFRESH] Network error, waiting ${waitTime}ms before retry...`
      );
      await new Promise((resolve) => setTimeout(resolve, waitTime));
      console.log(
        `[TOKEN REFRESH] Retry wait completed, continuing to next attempt...`
      );
    }
  }

  // 모든 시도 실패
  console.error("[TOKEN REFRESH] All attempts failed");
  console.error("[TOKEN REFRESH] ========== FAILED ==========");
  throw lastError || new Error("Token refresh failed after all attempts");
};

// 토큰이 유효한지 확인하는 함수
export const isTokenValid = (token: string): boolean => {
  if (!token) {
    console.log("[TOKEN VALIDATION] No token provided");
    return false;
  }

  try {
    // JWT 토큰의 payload 부분을 디코드
    const payload = JSON.parse(atob(token.split(".")[1]));
    const currentTime = Date.now() / 1000;
    const timeUntilExpiry = payload.exp - currentTime;

    // 토큰 만료 시간 확인
    const isValid = payload.exp > currentTime;
    console.log(
      `[TOKEN VALIDATION] Token valid: ${isValid}, expires in: ${Math.floor(
        timeUntilExpiry
      )}s`
    );
    return isValid;
  } catch (error) {
    console.error("[TOKEN VALIDATION] Token validation failed:", error);
    return false;
  }
};

// 현재 토큰 가져오기
export const getCurrentToken = (): string | null => {
  return rootStore.token;
};

// 토큰이 곧 만료될 예정인지 확인 (5분 전)
export const isTokenExpiringSoon = (token: string): boolean => {
  if (!token) {
    console.log(
      "[TOKEN EXPIRY CHECK] No token, returning true (expiring soon)"
    );
    return true;
  }

  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    const currentTime = Date.now() / 1000;
    const fiveMinutes = 5 * 60; // 5분
    const timeUntilExpiry = payload.exp - currentTime;
    const isExpiringSoon = timeUntilExpiry < fiveMinutes;

    console.error(
      `[TOKEN EXPIRY CHECK] Expiring soon: ${isExpiringSoon}, time until expiry: ${Math.floor(
        timeUntilExpiry
      )}s`
    );
    return isExpiringSoon;
  } catch (error) {
    console.error("[TOKEN EXPIRY CHECK] Token expiration check failed:", error);
    return true;
  }
};

// 리프레시 토큰이 만료되었는지 확인
export const isRefreshTokenExpired = (refreshToken: string | null): boolean => {
  if (!refreshToken) {
    console.log(
      "[REFRESH TOKEN CHECK] No refresh token, returning true (expired)"
    );
    return true;
  }

  try {
    const payload = JSON.parse(atob(refreshToken.split(".")[1]));
    const currentTime = Date.now() / 1000;
    const timeUntilExpiry = payload.exp - currentTime;
    const isExpired = payload.exp <= currentTime;

    console.log(
      `[REFRESH TOKEN CHECK] Expired: ${isExpired}, time until expiry: ${Math.floor(
        timeUntilExpiry
      )}s`
    );
    return isExpired;
  } catch (error) {
    console.error(
      "[REFRESH TOKEN CHECK] Refresh token expiration check failed:",
      error
    );
    return true;
  }
};

// 토큰 갱신 시도 횟수 리셋 함수 (더 이상 사용되지 않지만 하위 호환성을 위해 유지)
export const resetRefreshAttempts = (): void => {
  // 이제 루프 기반으로 변경되어 더 이상 필요 없음
};
